import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Extension as ExtensionIcon,
  FileDownload as ExportIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  VpnKey as KeyIcon,
  Campaign as CampaignIcon,
  AttachMoney as CostIcon,
  CloudQueue as CloudQueueIcon,
  MonitorHeart as UsageHealthIcon,
  Leaderboard as PerUserUsageIcon,
  BarChart as MostUsedToolsIcon,
  Groups as BrokerAdoptionIcon,
  PersonOff as InactiveUsersIcon,
  MilitaryTech as LeaderboardRankIcon,
  FilterAlt as ConversionFunnelIcon,
  Hub as ReferralSourcesIcon,
  RateReview as FeedbackIcon,
  AccountTree as SystemFlowsIcon,
  AccountBalance as EftPaymentsIcon,
  SwapVert as SubscriptionMovementsIcon,
  ReceiptLong as BulkInvoicesIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import { getVisibleMenuItems, NAV_ITEM_DESCRIPTIONS } from './nav-config';
import { IS_BROKER_ADMIN, IS_CONVERSION_REQUESTS_ENABLED } from '../config/admin-build';

const DRAWER_WIDTH = 288;
// Nav visibility gates live in ./nav-config so they're unit-testable without the
// view layer. HIDDEN_FOR_BROKER hides platform/internal pages (NDAs, Agreements,
// AWS Resources) on the broker build; SELF_SERVE_ONLY_PATHS hides the Conversion
// Funnel on the sales-led (IA) build (IA has no self-serve trials/subscriptions).

const menuItems = [
  { path: '/', label: 'Dashboard', icon: <DashboardIcon /> },
  { path: '/users', label: 'Users', icon: <PeopleIcon /> },
  { path: '/organizations', label: 'Organizations', icon: <BusinessIcon /> },
  { path: '/api-keys', label: 'API Keys', icon: <KeyIcon /> },
  { path: '/ndas', label: 'NDAs', icon: <DescriptionIcon /> },
  { path: '/agreements', label: 'Agreements', icon: <AssignmentIcon /> },
  { path: '/features', label: 'Features', icon: <ExtensionIcon /> },
  { path: '/notifications', label: 'Notifications', icon: <NotificationsIcon /> },
  // Feedback (Trello #204 follow-up) — end-of-trial reviews. Broker-admin
  // disposition: SHARED, NOT hidden — both stacks collect trial feedback and the
  // backend (GET /admin/feedback) scopes strictly by adminStack.
  { path: '/feedback', label: 'Feedback', icon: <FeedbackIcon /> },
  { path: '/whats-new', label: "What's New", icon: <CampaignIcon /> },
  // How It Works — internal flow-diagram docs explaining how the system works.
  // SHARED across both builds (no gate in nav-config) — both teams read it.
  { path: '/system-flows', label: 'How It Works', icon: <SystemFlowsIcon /> },
  { path: '/cost-report', label: 'Cost Report', icon: <CostIcon /> },
  // Broker-admin disposition (CLAUDE.md): SHARED, NOT hidden. Usage Health is a
  // commercial per-org dashboard like Cost Report (also visible to broker admins).
  // The backend (GET /admin/stats/usage-health) scopes strictly by adminStack — a
  // broker-ai admin sees only broker-ai orgs; there is no org-level admin scoping —
  // so cross-org visibility within the stack is intended and is exactly what the
  // "spot orgs running low" use case (Trello #246) requires.
  { path: '/usage-health', label: 'Usage Health', icon: <UsageHealthIcon /> },
  // Per-User Usage is the per-user complement to Usage Health (Trello #246 #2) —
  // same commercial intent, same broker-admin disposition: SHARED, NOT hidden. The
  // backend scopes by the admin's Cognito pool (= stack), so a broker admin only
  // ever sees broker-pool users.
  { path: '/per-user-usage', label: 'Per-User Usage', icon: <PerUserUsageIcon /> },
  // Most-Used Tools (Trello #246 #3) — what brokers actually run, stack-scoped by
  // the admin's Cognito pool like the other Health Dashboard pages. SHARED, NOT
  // hidden for broker admins (same commercial intent as Usage Health).
  { path: '/most-used-tools', label: 'Most-Used Tools', icon: <MostUsedToolsIcon /> },
  // Inactive Users (Trello #246 #6) — churn-risk view, stack-scoped like the
  // other Health Dashboard pages. SHARED, NOT hidden for broker admins.
  { path: '/inactive-users', label: 'Inactive Users', icon: <InactiveUsersIcon /> },
  // Activity Leaderboard (Trello #246 #4) — most-active users + orgs by runs,
  // stack-scoped like the other Health Dashboard pages. SHARED for broker admins.
  { path: '/leaderboard', label: 'Activity Leaderboard', icon: <LeaderboardRankIcon /> },
  // Broker AI Adoption — how many orgs have the Titan models enabled, ACROSS
  // BOTH stacks (intentionally cross-stack; internal admins only). Broker-AI ONLY
  // in the nav (gated via SELF_SERVE_ONLY_PATHS, like the Conversion Funnel).
  { path: '/broker-adoption', label: 'Broker AI Adoption', icon: <BrokerAdoptionIcon /> },
  // Trial & Conversion Funnel (Trello #246 #5) — sign-up → paid drop-off,
  // stack-scoped like the other Health Dashboard pages. SHARED for broker admins.
  { path: '/conversion-funnel', label: 'Conversion Funnel', icon: <ConversionFunnelIcon /> },
  // Referral Sources (Trello #260 Phase 2) — acquisition / conversion / revenue by
  // referral source. Broker-AI distribution-partner reporting, so broker-admin
  // ONLY (gated via SELF_SERVE_ONLY_PATHS, like the Conversion Funnel).
  { path: '/referral-sources', label: 'Referral Sources', icon: <ReferralSourcesIcon /> },
  // EFT Payments (Trello #299) — the review queue for broker EFT (bank-transfer)
  // subscription payments: an account manager confirms a payment reflected and
  // one-click activates the plan. Broker-AI ONLY (gated via SELF_SERVE_ONLY_PATHS,
  // like Referral Sources) — IA is sales-led with no self-serve EFT channel.
  { path: '/eft-payments', label: 'EFT Payments', icon: <EftPaymentsIcon /> },
  // Subscription Movements (Trello #365) — cancellations, downgrades and upgrades,
  // so a change in the paying-org count has an answer (who moved, from what plan,
  // and why). Broker-AI ONLY (gated via SELF_SERVE_ONLY_PATHS) — self-serve Stitch
  // subscriptions exist on no other stack.
  { path: '/subscription-movements', label: 'Subscription Movements', icon: <SubscriptionMovementsIcon /> },
  // Conversion Requests — team-account requests from capped users in shared
  // testing orgs (one-click approve & provision a pooled billable org + move the
  // user in). Broker-AI ONLY (gated via SELF_SERVE_ONLY_PATHS, like the funnel).
  // Reuses AssignmentIcon (Agreements is broker-hidden, so no double-render).
  // Master flag OFF (default) ⇒ the whole capture mechanism is dark — omit the
  // nav item entirely (the route also redirects). Flip VITE_ENABLE_CONVERSION_REQUESTS
  // with the backend CONVERSION_CAPTURE_ENABLED_STACKS to go live.
  ...(IS_CONVERSION_REQUESTS_ENABLED
    ? [{ path: '/conversion-requests', label: 'Conversion Requests', icon: <AssignmentIcon /> }]
    : []),
  { path: '/aws-health', label: 'AWS Resources', icon: <CloudQueueIcon /> },
  // Shown on both builds — invoicing is not stack-specific (Trello #363).
  { path: '/bulk-invoices', label: 'Bulk Invoices', icon: <BulkInvoicesIcon /> },
  { path: '/export', label: 'Export', icon: <ExportIcon /> },
];

const visibleMenuItems = getVisibleMenuItems(menuItems, { isBrokerAdmin: IS_BROKER_ADMIN });

const Layout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleMenuClose();
    await signOut();
    navigate('/login');
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
          Admin Dashboard
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {IS_BROKER_ADMIN ? 'Broker AI' : 'Intelligent Automation'}
        </Typography>
      </Box>

      <List sx={{ flex: 1, pt: 2 }}>
        {visibleMenuItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path !== '/' && location.pathname.startsWith(item.path));
          
          return (
            <ListItem key={item.path} disablePadding sx={{ px: 2, mb: 0.5 }}>
              <Tooltip
                title={NAV_ITEM_DESCRIPTIONS[item.path] ?? ''}
                placement="right"
                arrow
                enterDelay={400}
                enterNextDelay={400}
              >
                <ListItemButton
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) setMobileOpen(false);
                  }}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: isActive ? 'primary.main' : 'transparent',
                    color: isActive ? 'white' : 'text.primary',
                    '&:hover': {
                      backgroundColor: isActive ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: isActive ? 'white' : 'text.secondary', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontWeight: isActive ? 600 : 400 }}
                  />
                </ListItemButton>
              </Tooltip>
            </ListItem>
          );
        })}
      </List>

      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          Logged in as
        </Typography>
        <Typography variant="body2" fontWeight={500} noWrap>
          {user?.email}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { md: `${DRAWER_WIDTH}px` },
          backgroundColor: 'background.paper',
          color: 'text.primary',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }} />

          <NotificationBell />

          <IconButton onClick={handleMenuOpen}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
              {user?.firstName?.[0] || user?.email?.[0]?.toUpperCase() || 'A'}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem disabled>
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText 
                primary={user?.firstName ? `${user.firstName} ${user.lastName || ''}` : user?.email}
                secondary="Administrator"
              />
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleSignOut}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Sign Out" />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: DRAWER_WIDTH,
              borderRight: 'none',
              boxShadow: '4px 0 24px rgb(0 0 0 / 0.1)',
            },
          }}
        >
          {drawer}
        </Drawer>
        
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: DRAWER_WIDTH,
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: '64px',
          backgroundColor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;

